<template>
<div class="order-details-component">
  <section class="order-details-component__section">
    <h4 class="order-details-component__section-header">Статус</h4>
    <div class="order-details-component__statuses"
         :class="{ 'order-details-component__statuses--too-long-for-horizontal': fullStatuses.length > 4 }"
    >
      <p v-for="p in fullStatuses"
         :key="`${order.id}_${p.status_id}-status`"
         :aria-checked="p.completed_at !== null"
         class="order-details-component__status"
         :class="{ 'order-details-component__status--bad': isStatusBad(p) }"
      >
        <span class="order-details-component__status-checkmark">
          <i class="fa fa-xs"  :class="{ [isStatusBad(p) ? 'fa-times' : 'fa-check']: true }" />
        </span>
        <span class="order-details-component__status-text">{{ p.status && p.status.name || '...' }}</span>
      </p>
    </div>
  </section>
  <section class="order-details-component__section">
    <h4 class="order-details-component__section-header">Состав заказа</h4>
    <div><!-- wrapper div so we can safely set table width to 100% -->
      <table class="order-details-component__variants-table">
        <tbody>
          <template v-for="(v, ind) of order.variants">
            <tr :key="`${order.id}-${v.product_variant_id}-${v.modifiers_hash || ''}-${v.promotion_reward_id || ''}`"
                class="order-details-component__variant"
            >
              <td class="order-details-component__variant-number">{{ ind + 1 }}</td>
              <td class="order-details-component__variant-name">
                <router-link :to="getProductRoute(v)" class="underline">
                  {{ v.product.name }}
                </router-link>
              </td>
              <td class="order-details-component__variant-quantity">
                <template v-if="v.has_measured_quantity">{{ v.measured_quantity }}</template>
                <template v-else>{{ v.quantity }}</template>
                {{ v.product.unit_type.short_name }}
              </td>
              <td class="order-details-component__variant-price">
                <template v-if="v.promotion_reward_id !== null">
                  <strong class="text-primary-darker text-sm">
                    <i class="fal fa-gift"></i> Подарок
                  </strong>
                </template>
                <template v-else>{{ priceToText(v.total_price) }}</template>
              </td>
            </tr>
            <tr v-if="v.product.description || hasProductVariantModifiers(v)"
                :key="`${order.id}_${v.product_variant_id}-${v.modifiers_hash || ''}-${v.promotion_reward_id || ''}-description`"
                class="order-details-component__variant-description"
            >
              <td></td>
              <td colspan="99">
                {{ getProductVariantDescription(v) }}

                <span v-if="hasProductVariantModifiers(v)" class="order-details-component__variant-modifiers block">
                  <template v-for="modifier in getProductVariantModifiersList(v)">
                    <template v-if="modifier.hasGroup">{{ modifier.group }}: </template>{{ modifier.modifier }}
                    <template v-if="modifier.quantity > 1">({{ modifier.quantity }})</template>
                    <span class="text-primary-darker font-bold"
                          v-if="modifier.total_price !== null && modifier.total_price.value > 0 && v.promotion_reward_id === null"
                    >
                      +{{ modifier.total_price.full }}
                    </span>
                    <br>
                  </template>
                </span>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
    <hr class="order-details-component__products-and-payments-summary-divider">
    <div>
      <table class="order-details-component__payments-summary">
        <tbody>
          <tr class="order-details-component__total">
            <td>Заказ на сумму</td>
            <td>
              <template v-if="order.has_discount">
                {{ order.original_total_price.full }}
              </template>
              <template v-else>
                {{ order.total_price.full }}
              </template>
            </td>
          </tr>
          <tr v-if="order.has_discount" class="order-details-component__total">
            <td>Скидка</td>
            <td>-{{ order.discount_difference.full }}</td>
          </tr>
          <tr v-if="order.has_discount" class="order-details-component__total font-bold">
            <td>Итого к оплате</td>
            <td>{{ order.total_price.full }}</td>
          </tr>
          <tr><td>&nbsp;</td></tr>
          <tr>
            <td class="font-bold">Оплата:</td>
            <td></td>
          </tr>
          <tr v-for="payment in order.payments">
            <td>
              {{ payment.method.name }}
              <div v-if="isPendingOnlineTransaction(payment)" class="mt-2">
                <a href="javascript:;"
                   class="font-bold text-primary hover:underline hover:text-primary-light"
                   @click="startOnlinePayment(payment)"
                >
                  <template v-if="onlinePaymentState[payment.id]">
                    Подождите <i class="fal fa-spinner fa-spin" v-if=""></i>
                  </template>
                  <template v-else>
                    Оплатить
                  </template>
                </a>
                <p v-if="onlinePaymentErrors[payment.id]" class="text-danger font-bold mt-2">
                  Не удалось провести оплату.
                </p>
              </div>
              <div v-else-if="isCloudPaymentsTransaction(payment) || isCardTokenTransaction(payment)" class="mt-2">
                <p class="text-xs font-bold">{{ payment.status.name }}</p>
              </div>
            </td>
            <td class="align-top">{{ payment.amount.full }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
  <section v-if="order.deliver_at_local !== null" class="order-details-component__section">
    <h4 class="order-details-component__section-header">Дата и время</h4>
    <p class="order-details-component__section-text">{{ order.deliver_at_local.datetime_at }}</p>
  </section>
  <section v-else-if="order.deliver_at !== null" class="order-details-component__section">
    <h4 class="order-details-component__section-header">Дата и время</h4>
    <p class="order-details-component__section-text">{{ order.deliver_at.datetime_at }}</p>
  </section>
  <section class="order-details-component__section">
    <template v-if="order.delivery_method.id === 'pickup'">
      <h4 class="order-details-component__section-header">Самовывоз</h4>
      <p v-if="order.stock.display_name || order.stock.name" class="mb-4">
        Точка самовывоза: <strong>{{ order.stock.display_name || order.stock.name }}</strong>
      </p>
    </template>
    <template v-else-if="order.delivery_method.id === 'on_premise'">
      <h4 class="order-details-component__section-header">В заведении</h4>
      <p v-if="order.stock.display_name || order.stock.name" :class="{ 'mb-4': !hasOnPremiseTable }">
        Заведение:
        {{ order.stock.display_name || order.stock.name }}
      </p>
      <p v-if="hasOnPremiseTable" class="mb-4">
        № стола: <strong>{{ order.meta_data.on_premise.table }}</strong>.
      </p>
    </template>
    <template v-else>
      <h4 class="order-details-component__section-header">Адрес доставки</h4>
      <p class="order-details-component__section-text">
        {{ order.delivery_address && addressToString(order.delivery_address) || 'Не указан' }}
      </p>
    </template>
  </section>
  <section v-if="notesText" class="order-details-component__section">
    <h4 class="order-details-component__section-header">Комментарий к заказу</h4>
    <p class="order-details-component__section-text order-details-component__notes">{{ notesText }}</p>
  </section>
  <section v-if="orderRepeatsEnabled || order.can_cancel" class="order-details-component__section">
    <h4 class="order-details-component__section-header">Действия с заказом</h4>
    <p class="order-details-component__section-text">
      <a v-if="orderRepeatsEnabled" href="javascript:;" class="font-bold text-primary hover:underline hover:text-primary-light" @click="showOrderRepeatModal">
        Повторить заказ
      </a>
      <span v-if="orderRepeatsEnabled && order.can_cancel" class="text-grey-light mx-1 font-light">
        &bull;
      </span>
      <a v-if="order.can_cancel" href="javascript:;" class="font-bold text-danger hover:underline hover:text-danger-light" @click="showOrderCancelModal">
        Отменить заказ
      </a>
    </p>
  </section>
</div>
</template>

<script>
import { variantDescription } from '@/helpers/products'
import addressToString from '@/helpers/addressToString';
import priceToText from '@/helpers/priceToText';
import getBonusesPaymentAmount from '@/helpers/getBonusesPaymentAmount'
import badOrderStatusKinds from '@/helpers/badOrderStatusKinds.json'
import HasCurrencyData from '@/mixins/HasCurrencyData'
import CloudpaymentsProcessor from "@/components/payments/CloudpaymentsProcessor";
import HasProductVariantDescriptions from '@/mixins/HasProductVariantDescriptions'
import { mapActions, mapGetters } from 'vuex';
import storage from '@/storage';

export default {
  props: {
    order: {
      required: true,
      type: Object,
    },
  },

  mixins: [HasProductVariantDescriptions, HasCurrencyData],

  computed: {
    ...mapGetters({
      orderRepeatsEnabled: 'store/orderRepeatsEnabled',
      tabId: 'ui/tabId',
    }),
    fullStatuses() {
      return this.order.statuses_progress.map(p => ({
        ...p,
        // Yeah, `status` objects are included by default, but let's future-prove it.
        status: this.$store.state.orderStatuses.orderStatuses?.find(s => s.id === p.status_id) || null,
      }))
    },

    realMoneyTransaction() {
      const realMoneyPayments = this.order.payments.filter(p => p.method.id !== 'bonuses');

      if (realMoneyPayments.length !== 1) {
        // TODO make it handle `0` and `> 1` cases.
        console.warn('Real money transactions count is not `1`. Not printing the sum.');

        return null;
      }

      return realMoneyPayments[0];
    },

    bonusesPaymentAmount() {
      return getBonusesPaymentAmount(this.order.payments);
    },

    notesText() {
      let text = this.order.notes;
      const changeAmount = this.realMoneyTransaction?.meta.change;

      if (changeAmount?.value > 0) {
        if (text !== '') text += '\n';
        // TODO 'Сдача c' instead of this?
        text += `Сдача – ${priceToText(changeAmount)}`;
      }

      return text;
    },

    hasOnPremiseTable() {
      if (!this.order.meta_data) {
        return false
      } else if (!this.order.meta_data.on_premise) {
        return false
      }

      return !!this.order.meta_data.on_premise.table
    },
  },

  methods: {
    ...mapActions({
      showBroadcastMessage: 'store/showBroadcastMessage',
      repeatOrder: 'order/repeatOrder',
      cancelOrder: 'order/cancelOrder',
      addNotification: 'notifications/addNotification',
    }),
    variantDescription,
    priceToText,
    addressToString,

    isStatusBad(s) {
      return s.status && badOrderStatusKinds.includes(s.status.kind.id);
    },

    isPendingOnlineTransaction(transaction) {
      if (transaction.status.id !== 'pending') {
        return false
      }

      return this.isCloudPaymentsTransaction(transaction)
    },

    isCloudPaymentsTransaction(transaction) {
      return ['cloudpayments'].indexOf(transaction.method.id) !== -1
    },

    isCardTokenTransaction(transaction) {
      return transaction.method.id === 'card_token'
    },

    startOnlinePayment(transaction) {
      if (!this.isPendingOnlineTransaction(transaction)) {
        return
      } else if (this.onlinePaymentState[transaction.id] === true) {
        return
      }

      this.$set(this.onlinePaymentState, transaction.id, true)
      this.$set(this.onlinePaymentErrors, transaction.id, false)

      const processor = new CloudpaymentsProcessor(
        document,
        transaction,
        this.order.id,
        this.order.token
      )

      processor.start()
        .then(() => {
          location.reload()
        })
        .catch(err => {
          this.$set(this.onlinePaymentErrors, transaction.id, true)
          this.$set(this.onlinePaymentState, transaction.id, false)
        })
    },

    getProductRoute(variant) {
      const params = {
        name: 'product',
        params: {
          productId: variant.product.short_id,
          productSlug: variant.product.slug,
          promotionId: variant.promotion_id,
          rewardId: variant.promotion_reward_id,
        },
      }

      if (variant.promotion_id && variant.promotion_reward_id) {
        params.query = {
          promotion_id: variant.promotion_id,
          reward_id: variant.promotion_reward_id,
        }
      }

      return params
    },

    showOrderRepeatModal() {
      this.showBroadcastMessage({
        id: `OrderRepeatMessage:${this.order.id}:${Math.random().toString().replace('.', '')}`,
        type: { id: 'modal' },
        header: 'Повтор заказа',
        message: [
          `<p>Вы можете повторить ранее оформленный заказ. Ваша текущая корзина будет очищена и в неё будут скопированы все товары из выбранного заказа. Если какие-то товары более недоступны для заказа, они не будут скопированы.</p>`,
        ].join(`\n`),
        immediate: true,
        action: {
          button: 'Повторить заказ',
          cancel: 'Отмена',
          handler: async () => {
            const result = await this.repeatOrder(this.order.id);

            if (!result) {
              return;
            }

            storage.repeatedFromTabId(this.tabId);

            this.addNotification({
              id: Math.random().toString().replace('.', ''),
              type: 'success',
              title: 'Заказ скопирован',
              description: 'Корзина была успешно обновлена.',
              timeout: 5000,
            });
          },
        },
      });
    },

    showOrderCancelModal() {
      this.showBroadcastMessage({
        id: `OrderCancelMessage:${this.order.id}:${Math.random().toString().replace('.', '')}`,
        type: { id: 'modal' },
        header: 'Отмена заказа',
        message: [
          `<p>Вы уверены, что хотите отменить этот заказ?</p>`,
        ].join(`\n`),
        immediate: true,
        action: {
          button: 'Отменить заказ',
          cancel: 'Не отменять',
          handler: async () => {
            const result = await this.cancelOrder(this.order.id);

            if (!result) {
              return;
            }

            this.addNotification({
              id: Math.random().toString().replace('.', ''),
              type: 'success',
              title: 'Заказ отменён',
              description: 'Заказ был успешно отменён.',
              timeout: 5000,
            });
          },
        },
      });
    },
  },

  data() {
    return {
      onlinePaymentState: {},
      onlinePaymentErrors: {},
    }
  }
};
</script>

<style lang="less">
@r: order-details-component;
.@{r} {
  @apply overflow-auto;
  @tables-cells-padding-x: theme('padding.2');
  @variants-table-enumerator-column-min-width: theme('width.6');
  &__section {
    @apply py-px px-5;
    &:not(:first-child) {
      @apply border-t;
    }
  }
  &__section-header {
    @apply font-semibold my-3;
  }
  &__section-text {
    @apply my-3;
  }
  &__statuses {
    @apply flex flex-col my-4;
  }
  &__status {
    @apply whitespace-no-wrap;
    display: grid;
    grid-template-columns: min-content min-content;
    justify-items: center;
    // column-gap: theme('margin.1');
    .@{r}__status-checkmark {
      @apply rounded-full bg-light w-4 h-4 flex items-center justify-center text-grey-dark;
    }
    .@{r}__status-text {
      @apply ml-1 font-semibold text-grey-dark;
    }
    &[aria-checked="true"] {
      .@{r}__status-checkmark {
        @apply text-success-dark bg-success-lighter;
      }
      &.@{r}__status--bad {
        .@{r}__status-checkmark {
          @apply text-danger-dark bg-danger-lighter;
        }
      }
      .@{r}__status-text {
        color: inherit;
      }
    }
    &:not(:last-child)::after {
      content: "";
      @apply w-px h-4 my-2 block border rounded-full;
    }
  }
  &__variants-table {
    @apply my-3 w-full;
  }
  &__variant {
    > td {
      padding: @tables-cells-padding-x;
      &.@{r}__variant-number {
        @apply text-right text-dark-light pl-0 align-top;
        min-width: @variants-table-enumerator-column-min-width;
        width: 1px; // So it's not stretched when there is little content in other cells.
      }
      &.@{r}__variant-name {
        > a {
          color: inherit;
        }
      }
    }
  }
  &__variant-price,
  &__variant-quantity {
    @apply text-right whitespace-no-wrap;
  }
  &__variant-description {
    @apply text-dark-light font-light;
    > td {
      @apply px-2;
    }
  }
  &__products-and-payments-summary-divider {
    @apply border-grey;
    margin-left: calc(@variants-table-enumerator-column-min-width + @tables-cells-padding-x);
  }
  &__payments-summary {
    @apply my-3 w-full text-sm font-light;
    > tbody > tr > td {
      padding: @tables-cells-padding-x;
      @apply py-1;
      &:first-child {
        padding-left: calc(@variants-table-enumerator-column-min-width + @tables-cells-padding-x);
      }
      &:last-child {
        @apply text-right;
      }
    }
  }
  &__real-money-amount {
    @apply font-bold;
  }
  &__notes {
    @apply whitespace-pre-wrap;
  }
}

@screen sm {
  .@{r} {
    &__statuses {
      &:not(&--too-long-for-horizontal) {
        @apply flex-row
          flex-wrap; // Just in case.
        .@{r}__status {
          grid-template-columns: min-content min-content min-content;
          &:not(:last-child)::after {
            content: "";
            @apply h-px w-4 my-0 mx-2 self-center;
          }
        }
      }
    }
  }
}
</style>
